import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
  Braces,
  Database,
  LayoutTemplate,
  Plug,
  Plus,
  Search,
  Server,
  Sparkles,
  Table2,
  Workflow,
  Wrench,
  Zap,
} from "lucide-react";
import type { ComponentType } from "react";
import type { MentionableItem, MentionablesCatalog, MentionKind } from "@cometchat-ai/core";

import { cn } from "../../lib/utils.js";

export interface MentionSuggestRef {
  /** Returns true if the key was handled (caller should stop propagation). */
  onKeyDown: (e: KeyboardEvent) => boolean;
}

export interface MentionSuggestProps {
  /** Full workspace catalog (for the category sidebar + counts). */
  catalog: MentionablesCatalog;
  /** Initial typeahead query (text typed after the trigger char before the
   *  palette took over). The palette's own search input owns it from then on. */
  query: string;
  command: (item: MentionableItem) => void;
  /** Close the palette (Escape from the search input). */
  onClose?: () => void;
  /** Create a brand-new mentionable (context/memory) from the popup. Resolves
   *  to the new item (which is then inserted) or null if it failed/cancelled. */
  onCreate?: (kind: MentionKind, name: string) => Promise<MentionableItem | null>;
  /** When workspace long-term memory is off, the Memory category is hidden
   *  from the picker entirely. Defaults to shown. */
  memoryEnabled?: boolean;
  /** Per-category tab icon overrides (keyed by category id). Lets the host app
   *  swap the lucide defaults for its own icon set (e.g. the dashboard's
   *  iconsax glyphs, matching the settings sidebar). `variant`/`fill` are passed
   *  on the active tab so iconsax (`Bold`) and lucide (`fill`) both render filled. */
  categoryIcons?: Record<
    string,
    ComponentType<{ className?: string; variant?: string; fill?: string }>
  >;
}

const isMcp = (it: MentionableItem) => it.type === "mcp_server";
/**
 * Workflows attach to an agent as a skill with `type: "workflow"` (see
 * `Skill.type='workflow'` in the design). They ride the `skills` catalog list
 * like MCP servers and get their own category, split out of Skills & Tools.
 */
const isWorkflow = (it: MentionableItem) => it.type === "workflow";

/**
 * Sidebar categories. MCP servers are `Skill` rows with `type: "mcp_server"`,
 * and workflows `type: "workflow"`, so both share the `skills` catalog list —
 * we split them into their own categories (and exclude them from Skills &
 * Tools) via per-category getters.
 */
const CATEGORIES: {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string; fill?: string }>;
  items: (c: MentionablesCatalog) => MentionableItem[];
}[] = [
  // Each getter coalesces to `[]`: the catalog is external data (API response /
  // EMPTY fallback), so a missing category array (version skew, partial payload)
  // must degrade to an empty group, never crash the whole palette on `.filter`.
  {
    id: "skills",
    label: "Skills & Tools",
    icon: Wrench,
    items: (c) => (c.skills ?? []).filter((s) => !isMcp(s) && !isWorkflow(s)),
  },
  {
    id: "workflows",
    label: "Workflows",
    icon: Workflow,
    items: (c) => (c.skills ?? []).filter(isWorkflow),
  },
  { id: "mcp", label: "MCP", icon: Plug, items: (c) => (c.skills ?? []).filter(isMcp) },
  {
    id: "knowledgeBases",
    label: "Knowledge",
    icon: Database,
    items: (c) => c.knowledgeBases ?? [],
  },
  { id: "tables", label: "Tables", icon: Table2, items: (c) => c.tables ?? [] },
  { id: "queries", label: "Queries", icon: Server, items: (c) => c.queries ?? [] },
  {
    id: "contextVariables",
    label: "Context Variables",
    icon: Braces,
    items: (c) => c.contextVariables ?? [],
  },
  { id: "memoryVariables", label: "Memory", icon: Sparkles, items: (c) => c.memoryVariables ?? [] },
  { id: "cards", label: "Cards", icon: LayoutTemplate, items: (c) => c.cards ?? [] },
  { id: "actions", label: "Actions", icon: Zap, items: (c) => c.actions ?? [] },
];

/** Categories the popup can create new entries in, with their mention kind. */
const CREATE_CONFIG: Record<string, { kind: MentionKind; singular: string } | undefined> = {
  contextVariables: { kind: "context", singular: "context variable" },
  memoryVariables: { kind: "memory", singular: "memory variable" },
};

/** Mention-kind (typed before the `/`) → category to lock onto. `skill`/`tool`
 *  are omitted on purpose: an MCP server is a skill, so we search across all
 *  categories rather than hide it under one. */
const KIND_TO_CAT: Record<string, string> = {
  kb: "knowledgeBases",
  table: "tables",
  query: "queries",
  context: "contextVariables",
  memory: "memoryVariables",
  card: "cards",
  action: "actions",
};

/**
 * Interpret the typeahead query, which may be a bare term ("prod") OR a
 * canonical token the user typed/pasted ("table/products:update."). For the
 * latter we lock onto the kind's category and match against the slug only —
 * dropping the `:modifier` and any trailing punctuation — so pasting a full
 * `#kind/slug` still finds its entity instead of showing "0 items".
 */
function parseQuery(raw: string): { categoryId?: string; term: string; name: string } {
  const trimmed = raw.trim();
  const slash = trimmed.indexOf("/");
  if (slash === -1) return { term: trimmed.toLowerCase(), name: trimmed };
  const categoryId = KIND_TO_CAT[trimmed.slice(0, slash).toLowerCase()];
  const rest = trimmed
    .slice(slash + 1)
    .split(":")[0]! // drop the :modifier
    .replace(/[^\w.-]+$/, ""); // drop trailing punctuation (e.g. the ".")
  return { categoryId, term: rest.toLowerCase(), name: rest };
}

const matches = (it: MentionableItem, q: string) =>
  !q || it.slug.toLowerCase().includes(q) || it.displayName.toLowerCase().includes(q);

/** Flatten a catalog into one filtered list (keyboard order = sidebar order).
 *  When `memoryEnabled` is false the Memory category is excluded so its items
 *  are neither listed nor keyboard-selectable. */
export function flattenCatalog(
  catalog: MentionablesCatalog,
  query: string,
  memoryEnabled = true,
): MentionableItem[] {
  const q = query.trim().toLowerCase();
  const cats = memoryEnabled ? CATEGORIES : CATEGORIES.filter((c) => c.id !== "memoryVariables");
  return cats.flatMap((c) => c.items(catalog)).filter((it) => matches(it, q));
}

/**
 * Command-palette popup (ENG-36201) — centered modal styled after the product:
 * a search header (showing the live query), a CATEGORIES sidebar with per-group
 * counts, an items panel for the active category (with an inline "create" row
 * for context variables + memory), and a keyboard-nav footer.
 * ←/→ switch category · ↑/↓ navigate items · ↵ select/create · esc close.
 */
export const MentionSuggestList = forwardRef<MentionSuggestRef, MentionSuggestProps>(
  function MentionSuggestList(
    { catalog, query, command, onClose, onCreate, memoryEnabled = true, categoryIcons },
    ref,
  ) {
    // The palette owns the search text via its own input (seeded from whatever
    // was typed after the trigger char). Typing here never touches the prompt,
    // so the editor only ever holds the bare trigger until an item is chosen.
    const [localQuery, setLocalQuery] = useState(() => query);
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
      // Defer one tick so the node is in the DOM (the popup is portaled into a
      // body overlay after render) before we steal focus into the search box.
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }, []);

    const { categoryId, term, name } = useMemo(() => parseQuery(localQuery), [localQuery]);

    // Drop the Memory category when workspace memory is off.
    const categories = useMemo(
      () => (memoryEnabled ? CATEGORIES : CATEGORIES.filter((c) => c.id !== "memoryVariables")),
      [memoryEnabled],
    );

    // Per-category filtered items + counts. When the query named a kind
    // (`table/…`), only that category matches; otherwise search across all.
    const groups = useMemo(
      () =>
        categories.map((c) => ({
          ...c,
          items:
            categoryId && c.id !== categoryId
              ? []
              : c.items(catalog).filter((it) => matches(it, term)),
        })),
      [catalog, term, categoryId, categories],
    );

    // Which category is active. Priority: an explicit `kind/` in the query
    // wins; else the user's pinned (arrowed/clicked) category sticks — so
    // typing a NAME to create a variable doesn't bounce you off a
    // create-capable category onto whatever text-matches; else the first
    // category with hits.
    const [pinnedCatId, setPinnedCatId] = useState<string | null>(null);
    const [itemIndex, setItemIndex] = useState(0);
    const [busy, setBusy] = useState(false);

    const catIndex = useMemo(() => {
      if (categoryId) {
        const i = categories.findIndex((c) => c.id === categoryId);
        if (i >= 0) return i;
      }
      if (pinnedCatId) {
        const i = categories.findIndex((c) => c.id === pinnedCatId);
        if (i >= 0) return i;
      }
      return Math.max(
        groups.findIndex((g) => g.items.length > 0),
        0,
      );
    }, [categoryId, pinnedCatId, groups]);

    // Reset the item highlight whenever the active category or query changes.
    useEffect(() => {
      setItemIndex(0);
    }, [catIndex, term]);

    // Pin a category the user explicitly chose (sidebar click / ←→ nav).
    const pickCat = (i: number) => {
      const id = groups[i]?.id;
      if (id) setPinnedCatId(id);
      setItemIndex(0);
    };

    const activeGroup = groups[catIndex];
    const activeItems = activeGroup?.items ?? [];
    const createCfg = activeGroup ? CREATE_CONFIG[activeGroup.id] : undefined;
    const canCreate = Boolean(onCreate && createCfg && name.length > 0);

    // Keep the highlighted item scrolled into view during keyboard nav.
    const activeItemRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
      activeItemRef.current?.scrollIntoView({ block: "nearest" });
    }, [itemIndex, catIndex]);

    const create = async () => {
      if (!onCreate || !createCfg || !name || busy) return;
      setBusy(true);
      try {
        const item = await onCreate(createCfg.kind, name);
        if (item) command(item);
      } finally {
        setBusy(false);
      }
    };

    // Shared keyboard nav for the search input AND the editor (imperative
    // handle). Returns true when the key was consumed. ←/→ only switch category
    // when the caret is at a query boundary so they still move within the text.
    const handleKey = (e: Pick<KeyboardEvent, "key" | "shiftKey">, atEdges = true): boolean => {
      if (busy) return true; // swallow keys while a create is in flight
      if (e.key === "ArrowDown") {
        setItemIndex((i) => (activeItems.length ? (i + 1) % activeItems.length : 0));
        return true;
      }
      if (e.key === "ArrowUp") {
        setItemIndex((i) =>
          activeItems.length ? (i - 1 + activeItems.length) % activeItems.length : 0,
        );
        return true;
      }
      if ((e.key === "ArrowRight" && atEdges) || (e.key === "Tab" && !e.shiftKey)) {
        pickCat((catIndex + 1) % groups.length);
        return true;
      }
      if ((e.key === "ArrowLeft" && atEdges) || (e.key === "Tab" && e.shiftKey)) {
        pickCat((catIndex - 1 + groups.length) % groups.length);
        return true;
      }
      if (e.key === "Enter") {
        const item = activeItems[itemIndex];
        if (item) command(item);
        else if (canCreate) void create();
        return true;
      }
      if (e.key === "Escape") {
        onClose?.();
        return true;
      }
      return false;
    };

    useImperativeHandle(ref, () => ({ onKeyDown: (e: KeyboardEvent) => handleKey(e) }));

    const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const el = e.currentTarget;
      // Let ←/→ move the caret within the text; only hijack them for category
      // switching when the caret sits at the matching edge of the query.
      const atEdges =
        e.key === "ArrowLeft"
          ? el.selectionStart === 0 && el.selectionEnd === 0
          : e.key === "ArrowRight"
            ? el.selectionStart === el.value.length && el.selectionEnd === el.value.length
            : true;
      if (handleKey(e, atEdges)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const ActiveIcon =
      (activeGroup ? categoryIcons?.[activeGroup.id] : undefined) ?? activeGroup?.icon ?? Search;

    return (
      <div
        className="w-[760px] max-w-[92vw] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg"
        data-testid="mention-command-palette"
      >
        {/* Search header — a real, auto-focused input that drives filtering. */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <input
            ref={inputRef}
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Search all variables, actions, tools…"
            aria-label="Search mentionables"
            data-testid="mention-command-search"
            className="h-6 flex-1 bg-transparent text-2sm text-foreground outline-none placeholder:text-[color:var(--color-fg-subtle)]"
          />
        </div>

        {/* Category tabs — horizontal, scrollable, below the search. */}
        <div className="flex items-center gap-0.5 overflow-x-auto border-b border-border px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {groups.map((g, i) => {
            const Icon = (categoryIcons?.[g.id] ?? g.icon) as ComponentType<{
              className?: string;
              variant?: string;
              fill?: string;
            }>;
            const active = i === catIndex;
            return (
              <button
                key={g.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickCat(i);
                }}
                className={cn(
                  "relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-1.5 py-2.5 text-2sm font-normal transition-colors",
                  active
                    ? "text-foreground after:absolute after:inset-x-1.5 after:-bottom-px after:h-0.5 after:rounded-full after:bg-foreground"
                    : "text-[color:var(--color-fg-muted)] hover:text-foreground",
                )}
              >
                <Icon
                  className="h-3.5 w-3.5"
                  aria-hidden="true"
                  variant={active ? "Bold" : "Outline"}
                  fill={active ? "currentColor" : "none"}
                />
                {g.label}
              </button>
            );
          })}
        </div>

        {/* Items — fixed height so the panel never changes size (list vs empty). */}
        <div className="h-60 overflow-auto p-2" onMouseLeave={() => setItemIndex(0)}>
          {activeItems.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-6 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-foreground">
                <ActiveIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-2sm font-medium text-foreground">
                  {name ? "No matches" : `No ${activeGroup?.label.toLowerCase()} yet`}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {createCfg
                    ? name
                      ? "Press ↵ to create it."
                      : `Type a name to create a ${createCfg.singular}.`
                    : name
                      ? "Try a different search."
                      : "Items you add will show up here to reference."}
                </p>
              </div>
            </div>
          ) : (
            activeItems.map((item, i) => (
              <button
                key={`${item.kind}/${item.slug}`}
                ref={i === itemIndex ? activeItemRef : undefined}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  command(item);
                }}
                onMouseEnter={() => setItemIndex(i)}
                className={cn(
                  "group/item flex h-9 w-full items-center gap-2.5 rounded-md px-2 text-left transition-colors",
                  i === itemIndex ? "bg-muted" : "hover:bg-muted/60",
                )}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  <ActiveIcon className="h-4 w-4 text-foreground" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1 truncate text-2sm font-normal text-foreground">
                  {item.displayName}
                </span>
                <span className="ml-auto flex shrink-0 items-center gap-1.5">
                  {item.state === "needs_setup" ? (
                    <span className="text-xs text-amber-600" title="Needs setup">
                      ⚠
                    </span>
                  ) : null}
                  <kbd
                    aria-hidden="true"
                    className={cn(
                      "h-5 items-center gap-1 rounded border border-border bg-card px-1.5 text-2xs leading-none text-muted-foreground",
                      i === itemIndex ? "inline-flex" : "hidden group-hover/item:inline-flex",
                    )}
                  >
                    <span className="flex translate-y-0.5 items-center text-sm leading-none">
                      ↵
                    </span>
                    Enter
                  </kbd>
                </span>
              </button>
            ))
          )}
        </div>

        {/* Inline create row — context variables + memory (ENG-36201). */}
        {createCfg ? (
          <div className="border-t border-border p-2">
            <button
              type="button"
              disabled={!canCreate}
              data-testid={`mention-create-${createCfg.kind}`}
              onMouseDown={(e) => {
                e.preventDefault();
                void create();
              }}
              className={cn(
                "flex h-9 w-full items-center gap-2.5 rounded-md px-2 text-left text-2sm transition-colors",
                canCreate
                  ? "text-foreground hover:bg-muted"
                  : "cursor-not-allowed text-muted-foreground",
              )}
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                <Plus className="h-4 w-4" />
              </span>
              <span className="min-w-0 truncate">
                {busy
                  ? `Creating ${createCfg.singular}…`
                  : name
                    ? `Create ${createCfg.singular} “${name}”`
                    : `Type a name to create a ${createCfg.singular}`}
              </span>
            </button>
          </div>
        ) : null}
      </div>
    );
  },
);
